// client/src/components/admin/DeliveryTab.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { logoPng } from '../../config/assets';
import { getNotificationApi } from '../../utils/safeNotification';

const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes nextPulse {
    0%   { box-shadow: 0 0 0 0 rgba(0,0,0,0.4); }
    70%  { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
    100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
  }
`;
if (!document.head.querySelector('[data-delivery-anim]')) {
  styleTag.setAttribute('data-delivery-anim', '1');
  document.head.appendChild(styleTag);
}

const today = new Date().toISOString().slice(0, 10);
const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function DeliveryTab({ api, restaurant }) {
  const [activeSection, setActiveSection] = useState('orders');
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);

  const prevOrderIds = useRef(new Set());

  const playBeep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.3].forEach(startTime => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 660;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, ctx.currentTime + startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startTime + 0.25);
        osc.start(ctx.currentTime + startTime);
        osc.stop(ctx.currentTime + startTime + 0.25);
      });
    } catch (e) {}
  }, []);

  const showMsg = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const qs = restaurant?.id ? `?restaurantId=${restaurant.id}` : '';
  useEffect(() => { fetchAll(); }, [restaurant?.id]); // eslint-disable-line

  const fetchStats = async (from = dateFrom, to = dateTo) => {
    setStatsLoading(true);
    try {
      const { data } = await api.get(
        `/api/admin/delivery/stats${qs}${qs ? '&' : '?'}from=${from}&to=${to}`
      );
      setStats(data);
    } catch (err) {
      console.error('fetchStats error:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/delivery/settings${qs}`);
      setSettings(data.settings);
      setZones(data.zones || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/delivery/orders${qs}`);
      const ordersList = data || [];
      const newOrders = ordersList.filter(o =>
        o.status === 'new' && !prevOrderIds.current.has(o.id)
      );
      if (newOrders.length > 0) {
        playBeep();
        const N = getNotificationApi();
        if (N && N.permission === 'granted') {
          // eslint-disable-next-line new-cap
          new N('🚗 Новый заказ доставки!', {
            body: newOrders.map(o =>
              `#${o.id} ${o.client_name} — ${Number(o.total).toLocaleString()} сум`
            ).join('\n'),
            icon: logoPng(),
          });
        }
      }
      prevOrderIds.current = new Set(ordersList.map(o => o.id));
      setOrders(ordersList);
    } catch (err) {
      console.error(err);
    }
  }, [api, qs, playBeep]);

  useEffect(() => {
    if (activeSection === 'orders') fetchOrders();
  }, [activeSection, fetchOrders]);

  useEffect(() => {
    if (activeSection !== 'orders') return;
    const iv = setInterval(fetchOrders, 10000);
    return () => clearInterval(iv);
  }, [activeSection, fetchOrders]);

  useEffect(() => {
    if (activeSection === 'stats') fetchStats();
  }, [activeSection]); // eslint-disable-line

  const fetchMenu = useCallback(async () => {
    if (!restaurant?.id) { console.log('❌ fetchMenu: нет restaurant.id'); return; }
    setMenuLoading(true);
    try {
      const { data } = await api.get(`/api/admin/delivery/menu?restaurantId=${restaurant.id}`);
      console.log('🍽️ fetchMenu data:', data, 'items:', data?.items);
      setMenuItems(data?.items || []);
    } catch (e) {
      console.error('❌ fetchMenu error:', e?.response?.status, e?.response?.data, e?.message);
      showMsg('Ошибка загрузки меню');
    } finally {
      setMenuLoading(false);
    }
  }, [api, restaurant?.id]);

  useEffect(() => {
    if (activeSection === 'menu') fetchMenu();
  }, [activeSection, fetchMenu]);

  if (loading) return <div style={s.loading}>⏳ Загрузка...</div>;

  return (
    <div>
      {message && <div style={s.message}>{message}</div>}

      <div style={s.subTabs}>
        {[
          { key: 'orders',   label: '📋 Заказы' },
          { key: 'menu',     label: '🍽️ Меню' },
          { key: 'stats',    label: '📊 Итоги' },
          { key: 'settings', label: '⚙️ Настройки' },
          { key: 'zones',    label: '🗺️ Зоны' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            style={{
              ...s.subTab,
              ...(activeSection === tab.key ? s.subTabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSection === 'stats' && (
        <DeliveryStatsSection
          stats={stats}
          loading={statsLoading}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateChange={(from, to) => {
            setDateFrom(from);
            setDateTo(to);
            fetchStats(from, to);
          }}
        />
      )}

      {activeSection === 'orders' && (
        <OrdersSection
          orders={orders}
          onRefresh={fetchOrders}
          api={api}
          showMsg={showMsg}
          restaurantId={restaurant?.id}
        />
      )}

      {activeSection === 'settings' && (
        <SettingsSection
          settings={settings}
          restaurant={restaurant}
          api={api}
          showMsg={showMsg}
          onRefresh={fetchAll}
        />
      )}

      {activeSection === 'zones' && (
        <ZonesSection
          zones={zones}
          api={api}
          showMsg={showMsg}
          onRefresh={fetchAll}
          restaurantId={restaurant?.id}
        />
      )}

      {activeSection === 'menu' && (
        <DeliveryMenuSection
          items={menuItems}
          loading={menuLoading}
          api={api}
          showMsg={showMsg}
          restaurantId={restaurant?.id}
          onRefresh={fetchMenu}
        />
      )}
    </div>
  );
}

// ─── Статус-бар прогресса заказа ───────────────────────────
function DeliveryStatusBar({ status, orderType, nextStatus, onStatusChange, isLoading }) {
  const [confirm, setConfirm] = useState(false);

  const stepsDelivery = [
    { key: 'new',        label: 'Принят',      icon: '✅', color: '#4CAF50' },
    { key: 'accepted',   label: 'Подтверждён', icon: '👍', color: '#2196F3' },
    { key: 'cooking',    label: 'Готовится',   icon: '🍳', color: '#9C27B0' },
    { key: 'ready',      label: 'Готов',       icon: '📦', color: '#FF9800' },
    { key: 'on_the_way', label: 'В пути',      icon: '🚗', color: '#00BCD4' },
    { key: 'delivered',  label: 'Доставлен',   icon: '🎉', color: '#4CAF50' },
  ];
  const stepsPickup = [
    { key: 'new',      label: 'Принят',      icon: '✅', color: '#4CAF50' },
    { key: 'accepted', label: 'Подтверждён', icon: '👍', color: '#2196F3' },
    { key: 'cooking',  label: 'Готовится',   icon: '🍳', color: '#9C27B0' },
    { key: 'ready',    label: 'Готово!',     icon: '📦', color: '#FF9800' },
  ];

  if (status === 'cancelled') return null;

  const steps = orderType === 'pickup' ? stepsPickup : stepsDelivery;
  const currentIdx = steps.findIndex(s => s.key === status);
  const nextStep = steps[currentIdx + 1];
  const canAdvance = nextStep && nextStep.key === nextStatus && !isLoading;

  const handleNextClick = () => {
    if (!canAdvance) return;
    setConfirm(true);
  };
  const handleConfirm = () => {
    setConfirm(false);
    onStatusChange(nextStep.key);
  };
  const handleCancel = () => setConfirm(false);

  return (
    <div style={{ marginBottom: '12px' }}>
      {/* Прогресс-шаги — только индикатор */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '10px 4px 6px',
        backgroundColor: '#F9F9F9', borderRadius: '12px',
        marginBottom: canAdvance ? '8px' : '0',
      }}>
        {steps.map((step, idx) => {
          const passed  = idx < currentIdx;
          const active  = idx === currentIdx;
          return (
            <React.Fragment key={step.key}>
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', flex: 1,
              }}>
                <div style={{
                  width: active ? '36px' : '28px',
                  height: active ? '36px' : '28px',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: active ? '16px' : '12px',
                  marginBottom: '3px',
                  backgroundColor: active ? step.color : (passed ? '#E8E8E8' : '#F0F0F0'),
                  color: active ? '#fff' : (passed ? '#999' : '#ccc'),
                  boxShadow: active ? `0 2px 8px ${step.color}66` : 'none',
                  transition: 'all 0.3s',
                  position: 'relative', zIndex: 1,
                }}>
                  {passed ? '✓' : step.icon}
                </div>
                <div style={{
                  fontSize: '9px', textAlign: 'center',
                  lineHeight: '1.2', maxWidth: '44px',
                  fontWeight: active ? '800' : '400',
                  color: active ? '#111' : (passed ? '#bbb' : '#ddd'),
                }}>
                  {step.label}
                </div>
              </div>
              {idx < steps.length - 1 && (
                <div style={{
                  height: '2px', flex: 1, marginBottom: '16px',
                  backgroundColor: idx < currentIdx ? '#E0E0E0' : '#F0F0F0',
                  transition: 'background-color 0.3s',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Кнопка перехода — отдельно */}
      {canAdvance && !confirm && (
        <button
          onClick={handleNextClick}
          disabled={isLoading}
          style={{
            width: '100%', padding: '10px',
            borderRadius: '10px', border: 'none',
            backgroundColor: nextStep.color,
            color: '#fff', fontSize: '13px', fontWeight: '700',
            cursor: 'pointer', fontFamily: 'inherit',
            opacity: isLoading ? 0.6 : 1,
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '6px',
          }}
        >
          {isLoading ? '⏳ Обновляем...' : `${nextStep.icon} Перевести → ${nextStep.label}`}
        </button>
      )}

      {/* Подтверждение */}
      {confirm && nextStep && (
        <div style={{
          backgroundColor: '#FFF8E1', borderRadius: '10px',
          padding: '10px 12px', border: '1px solid #FFE082',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: '8px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>
            ⚠️ Перевести в «{nextStep.label}»?
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleConfirm}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: 'none',
                backgroundColor: nextStep.color, color: '#fff',
                fontSize: '13px', fontWeight: '700',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Да</button>
            <button
              onClick={handleCancel}
              style={{
                padding: '6px 12px', borderRadius: '8px',
                border: '1px solid #ddd', backgroundColor: '#fff',
                fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Нет</button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersSection({ orders, onRefresh, api, showMsg, restaurantId }) {
  const [loadingIds, setLoadingIds] = useState({});

  // Менеджер/кухня: до ready. ready→on_the_way и on_the_way→delivered — только курьер
  const NEXT_STATUS = {
    new:      'accepted',
    accepted: 'cooking',
    cooking:  'ready',
  };

  const NEXT_STATUS_PICKUP = {
    new:      'accepted',
    accepted: 'cooking',
    cooking:  'ready',
  };

  const setLoading = (orderId, key, val) => {
    setLoadingIds(prev => ({ ...prev, [`${orderId}_${key}`]: val }));
  };
  const isLoading = (orderId, key) => !!loadingIds[`${orderId}_${key}`];

  const ordQs = restaurantId ? `?restaurantId=${restaurantId}` : '';

  const handleStatus = async (orderId, status) => {
    setLoading(orderId, 'status', true);
    try {
      await api.patch(`/api/admin/delivery/orders/${orderId}/status${ordQs}`, { status });
      showMsg('✅ Статус обновлён');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка обновления статуса');
    } finally {
      setLoading(orderId, 'status', false);
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Отменить заказ?')) return;
    setLoading(orderId, 'cancel', true);
    try {
      await api.patch(`/api/admin/delivery/orders/${orderId}/status${ordQs}`, { status: 'cancelled' });
      showMsg('✅ Заказ отменён');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка отмены');
    } finally {
      setLoading(orderId, 'cancel', false);
    }
  };

  const handleMarkPaid = async (orderId, clientName) => {
    if (!window.confirm(
      `Отметить заказ #${orderId} (${clientName}) как оплаченный?\n\nИспользуйте только если клиент оплатил вне системы.`
    )) return;

    setLoading(orderId, 'pay', true);
    try {
      await api.patch(`/api/admin/delivery/orders/${orderId}/mark-paid${ordQs}`);
      showMsg('✅ Оплата отмечена');
      onRefresh();
    } catch (err) {
      showMsg(err.response?.data?.error || '❌ Ошибка');
    } finally {
      setLoading(orderId, 'pay', false);
    }
  };

  if (orders.length === 0) {
    return (
      <div style={s.empty}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🚚</div>
        <div>Заказов доставки пока нет</div>
      </div>
    );
  }

  return (
    <div>
      {orders.map(order => {
        const nextStatus = order.order_type === 'pickup'
          ? NEXT_STATUS_PICKUP[order.status]
          : NEXT_STATUS[order.status];

        const needsManualPay =
          ['payme', 'click'].includes(order.payment_method) &&
          order.payment_status === 'waiting_payment' &&
          !['delivered', 'cancelled'].includes(order.status);

        const paymentLabel = {
          cash:  '💵 Наличные',
          click: '📱 Click',
          payme: '💳 Payme',
        }[order.payment_method] || order.payment_method;

        const paymentStatusBadge = order.payment_status === 'paid'
          ? { text: '✅ Оплачено',        bg: '#E8F5E9', color: '#2E7D32' }
          : order.payment_status === 'waiting_payment'
          ? { text: '⏳ Ожидает оплаты',  bg: '#FFF8E1', color: '#F57F17' }
          : order.payment_status === 'cancelled'
          ? { text: '❌ Оплата отменена', bg: '#FFEBEE', color: '#C62828' }
          : { text: '⏳ Ожидает',         bg: '#F5F5F5', color: '#666' };

        return (
          <div key={order.id} style={s.orderCard}>
            <div style={s.orderHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>
                  {order.order_type === 'pickup' ? '🏃' : '🚗'}
                </span>
                <div>
                  <div style={s.orderTitle}>
                    #{order.id} · {order.client_name}
                  </div>
                  <div style={s.orderMeta}>{order.client_phone}</div>
                </div>
              </div>
            </div>

            <DeliveryStatusBar
              status={order.status}
              orderType={order.order_type}
              nextStatus={nextStatus}
              onStatusChange={(s) => handleStatus(order.id, s)}
              isLoading={isLoading(order.id, 'status')}
            />

            {order.order_type === 'delivery' && order.address && (
              <div style={s.orderRow}>
                📍 {order.address}
                {order.zone_name && ` (${order.zone_name})`}
              </div>
            )}

            {order.delivery_time === 'scheduled' && order.scheduled_at && (
              <div style={s.orderRow}>
                🕐 Ко времени: {new Date(order.scheduled_at).toLocaleString('ru')}
              </div>
            )}

            {order.comment && (
              <div style={s.orderRow}>💬 {order.comment}</div>
            )}

            <div style={s.orderItems}>
              {(order.items || []).map((item, i) => (
                <div key={i} style={s.orderItem}>
                  <span>{item.name} × {item.quantity}</span>
                  <span>{Number(item.total).toLocaleString()} сум</span>
                </div>
              ))}
            </div>

            <div style={s.orderTotal}>
              <div style={{ fontSize: '13px', color: '#888' }}>
                Блюда: {Number(order.items_total).toLocaleString()} сум
                {order.delivery_price > 0 &&
                  ` + доставка: ${Number(order.delivery_price).toLocaleString()} сум`}
              </div>
              <div style={s.totalPrice}>
                {Number(order.total).toLocaleString()} сум
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: needsManualPay ? '6px' : '8px',
              flexWrap: 'wrap',
              gap: '6px',
            }}>
              <span style={{ fontSize: '13px', color: '#555' }}>
                {paymentLabel}
              </span>
              <span style={{
                fontSize: '12px',
                fontWeight: '600',
                padding: '3px 10px',
                borderRadius: '20px',
                backgroundColor: paymentStatusBadge.bg,
                color: paymentStatusBadge.color,
              }}>
                {paymentStatusBadge.text}
              </span>
            </div>

            {needsManualPay && (
              <button
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  border: '1px dashed #FFA000',
                  backgroundColor: '#FFFDE7',
                  color: '#E65100',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: isLoading(order.id, 'pay') ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: '8px',
                  opacity: isLoading(order.id, 'pay') ? 0.6 : 1,
                }}
                onClick={() => handleMarkPaid(order.id, order.client_name)}
                disabled={isLoading(order.id, 'pay')}
              >
                {isLoading(order.id, 'pay')
                  ? '⏳ Обновляем...'
                  : '💵 Отметить как оплаченный'}
              </button>
            )}

            {order.courier_name && (
              <div style={s.orderRow}>
                🧑 Курьер: {order.courier_name}
                {order.courier_phone && ` · ${order.courier_phone}`}
              </div>
            )}

            {!['delivered', 'cancelled'].includes(order.status) && (
              <div style={s.orderActions}>
                <button
                  style={{
                    ...s.btnCancel,
                    opacity: isLoading(order.id, 'cancel') ? 0.6 : 1,
                  }}
                  onClick={() => handleCancel(order.id)}
                  disabled={isLoading(order.id, 'cancel')}
                >
                  {isLoading(order.id, 'cancel') ? '⏳...' : 'Отменить'}
                </button>
              </div>
            )}

            <div style={s.orderTime}>
              {new Date(order.created_at).toLocaleString('ru')}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SettingsSection({ settings, restaurant, api, showMsg, onRefresh }) {
  const defaults = {
    is_enabled: false,
    delivery_enabled: true,
    pickup_enabled: true,
    working_hours_start: '10:00',
    working_hours_end: '22:00',
    min_order_amount: 0,
    delivery_time_min: 30,
    payment_cash: true,
    payment_click: false,
    payment_payme: false,
    phone: '',
    manager_phone: '',
  };

  const normalize = (s) => {
    if (!s) return { ...defaults };
    const merged = { ...defaults, ...s };
    return {
      ...merged,
      phone: merged.phone ?? '',
      manager_phone: merged.manager_phone ?? '',
    };
  };

  const [form, setForm] = useState(() => normalize(settings));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm(normalize(settings));
  }, [settings]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = restaurant?.id
        ? `/api/admin/delivery/settings?restaurantId=${restaurant.id}`
        : '/api/admin/delivery/settings';
      await api.put(url, form);
      showMsg('✅ Настройки сохранены');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const deliveryUrl = `${window.location.origin}/delivery/${restaurant?.slug || ''}`;

  return (
    <div style={s.section}>
      <div style={s.card}>
        <div style={s.cardTitle}>🔗 Страница доставки</div>
        <div style={s.deliveryUrl}>{deliveryUrl}</div>
        <p style={{ fontSize: '12px', color: '#888', lineHeight: 1.45, marginTop: '10px', marginBottom: 0 }}>
          В адресе — <strong>слаг</strong> ресторана (как в БД). Название в шапке для гостей (например «Demo») — другое поле; совпадать с слагом не обязано. Смена слага в настройках меняет только ссылку; старые закладки с прошлым слагом работать не будут.
        </p>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            style={s.btnSecondary}
            onClick={() => {
              navigator.clipboard.writeText(deliveryUrl);
              showMsg('✅ Ссылка скопирована');
            }}
          >
            📋 Скопировать
          </button>
          <button
            style={s.btnSecondary}
            onClick={() => window.open(deliveryUrl, '_blank')}
          >
            🔗 Открыть
          </button>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>⚙️ Основное</div>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={form.is_enabled}
            onChange={e => setForm({ ...form, is_enabled: e.target.checked })}
          />
          <span>Доставка включена</span>
        </label>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={form.delivery_enabled}
            onChange={e => setForm({ ...form, delivery_enabled: e.target.checked })}
          />
          <span>🚗 Курьерская доставка</span>
        </label>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={form.pickup_enabled}
            onChange={e => setForm({ ...form, pickup_enabled: e.target.checked })}
          />
          <span>🏃 Самовывоз</span>
        </label>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>🕐 Время работы доставки</div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={s.label}>С</div>
            <input
              type="time"
              style={s.input}
              value={form.working_hours_start || '10:00'}
              onChange={e => setForm({ ...form, working_hours_start: e.target.value })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={s.label}>До</div>
            <input
              type="time"
              style={s.input}
              value={form.working_hours_end || '22:00'}
              onChange={e => setForm({ ...form, working_hours_end: e.target.value })}
            />
          </div>
        </div>
        <div style={{ marginTop: '12px' }}>
          <div style={s.label}>Среднее время доставки (мин)</div>
          <input
            type="number"
            style={s.input}
            value={form.delivery_time_min || 30}
            onChange={e => setForm({ ...form, delivery_time_min: Number(e.target.value) })}
            placeholder="30"
          />
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>💰 Лимиты</div>
        <div style={s.label}>Минимальная сумма заказа (сум)</div>
        <input
          type="number"
          style={s.input}
          value={form.min_order_amount || 0}
          onChange={e => setForm({ ...form, min_order_amount: Number(e.target.value) })}
          placeholder="0"
        />
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>💳 Способы оплаты</div>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={form.payment_cash}
            onChange={e => setForm({ ...form, payment_cash: e.target.checked })}
          />
          <span>💵 Наличные курьеру</span>
        </label>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={form.payment_click}
            onChange={e => setForm({ ...form, payment_click: e.target.checked })}
          />
          <span>📱 Click</span>
        </label>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={form.payment_payme}
            onChange={e => setForm({ ...form, payment_payme: e.target.checked })}
          />
          <span>💳 Payme</span>
        </label>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>📞 Контактный телефон</div>
        <input
          type="tel"
          style={s.input}
          value={form.phone || ''}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          placeholder="+998 90 123 45 67"
        />
      </div>

      <div style={{
        backgroundColor: '#E3F2FD',
        borderRadius: '12px',
        padding: '14px 16px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
      }}>
        <span style={{ fontSize: '24px' }}>📨</span>
        <div>
          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
            Telegram уведомления
          </div>
          <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.5' }}>
            Уведомления о заказах доставки отправляются через бота из вкладки
            {' '}
            <strong>«Настройки → Telegram-бот»</strong>.
            {' '}
            Если бот настроен — уведомления придут автоматически.
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>👨‍💼 Телефон менеджера</div>
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>
          Клиент сможет позвонить при проблемах с заказом
        </div>
        <input
          type="tel"
          style={s.input}
          value={form.manager_phone || ''}
          onChange={e => setForm({ ...form, manager_phone: e.target.value })}
          placeholder="+998 90 123 45 67"
        />
      </div>

      <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
        {saving ? '⏳ Сохраняем...' : '💾 Сохранить настройки'}
      </button>
    </div>
  );
}

function ZonesSection({ zones, api, showMsg, onRefresh, restaurantId }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', price: '', min_order: '' });
  const [editForm, setEditForm] = useState({});

  const zoneQs = restaurantId ? `?restaurantId=${restaurantId}` : '';
  const handleAdd = async () => {
    if (!form.name.trim() || !form.price) {
      showMsg('❌ Введите название и цену');
      return;
    }
    try {
      await api.post(`/api/admin/delivery/zones${zoneQs}`, {
        name: form.name.trim(),
        price: Number(form.price),
        min_order: Number(form.min_order) || 0,
      });
      setForm({ name: '', price: '', min_order: '' });
      setShowAdd(false);
      showMsg('✅ Зона добавлена');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка');
    }
  };

  const handleEdit = async (id) => {
    try {
      await api.put(`/api/admin/delivery/zones/${id}${zoneQs}`, {
        name: editForm.name,
        price: Number(editForm.price),
        min_order: Number(editForm.min_order) || 0,
        is_active: editForm.is_active,
      });
      setEditId(null);
      showMsg('✅ Зона обновлена');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить зону "${name}"?`)) return;
    try {
      await api.delete(`/api/admin/delivery/zones/${id}${zoneQs}`);
      showMsg('✅ Зона удалена');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка');
    }
  };

  return (
    <div style={s.section}>
      <div style={s.card}>
        <div style={s.cardTitle}>🗺️ Зоны доставки</div>
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
          Укажите районы/зоны и цену доставки для каждой
        </div>

        {zones.length === 0 && (
          <div style={s.empty}>Нет зон — добавьте первую</div>
        )}

        {zones.map(zone => (
          <div key={zone.id} style={s.zoneRow}>
            {editId === zone.id ? (
              <div style={{ flex: 1 }}>
                <input
                  style={{ ...s.input, marginBottom: '6px' }}
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Название зоны"
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    style={s.input}
                    value={editForm.price}
                    onChange={e => setEditForm({ ...editForm, price: e.target.value })}
                    placeholder="Цена доставки"
                  />
                  <input
                    type="number"
                    style={s.input}
                    value={editForm.min_order}
                    onChange={e => setEditForm({ ...editForm, min_order: e.target.value })}
                    placeholder="Мин. заказ"
                  />
                </div>
                <label style={{ ...s.toggle, marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })}
                  />
                  <span>Активна</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button style={s.btnPrimary} onClick={() => handleEdit(zone.id)}>
                    Сохранить
                  </button>
                  <button style={s.btnSecondary} onClick={() => setEditId(null)}>
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '15px' }}>
                    {zone.name}
                    {!zone.is_active && (
                      <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px' }}>
                        (неактивна)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                    Доставка: {Number(zone.price).toLocaleString()} сум
                    {zone.min_order > 0 && ` · Мин. заказ: ${Number(zone.min_order).toLocaleString()} сум`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    style={s.btnIcon}
                    onClick={() => {
                      setEditId(zone.id);
                      setEditForm({
                        name: zone.name,
                        price: zone.price,
                        min_order: zone.min_order || 0,
                        is_active: zone.is_active,
                      });
                    }}
                  >✏️</button>
                  <button
                    style={{ ...s.btnIcon, backgroundColor: '#FFEBEE' }}
                    onClick={() => handleDelete(zone.id, zone.name)}
                  >🗑️</button>
                </div>
              </>
            )}
          </div>
        ))}

        {showAdd ? (
          <div style={s.addForm}>
            <input
              style={{ ...s.input, marginBottom: '8px' }}
              value={form.name}
              autoFocus
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Название зоны (напр. Центр, Юнусабад)"
            />
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="number"
                style={s.input}
                value={form.price}
                onChange={e => setForm({ ...form, price: e.target.value })}
                placeholder="Цена доставки (сум)"
              />
              <input
                type="number"
                style={s.input}
                value={form.min_order}
                onChange={e => setForm({ ...form, min_order: e.target.value })}
                placeholder="Мин. сумма заказа"
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={s.btnPrimary} onClick={handleAdd}>Добавить</button>
              <button style={s.btnSecondary} onClick={() => setShowAdd(false)}>Отмена</button>
            </div>
          </div>
        ) : (
          <button style={s.btnDashed} onClick={() => setShowAdd(true)}>
            + Добавить зону
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DeliveryStatsSection
// ─────────────────────────────────────────────────────────────
function DeliveryStatsSection({ stats, loading, dateFrom, dateTo, onDateChange }) {
  const [from, setFrom] = useState(dateFrom);
  const [to, setTo] = useState(dateTo);

  const fmt = (n) => Number(n || 0).toLocaleString('ru-RU');
  const apply = () => onDateChange(from, to);

  const periods = [
    {
      label: 'Сегодня',
      fn: () => {
        const d = new Date().toISOString().slice(0, 10);
        setFrom(d); setTo(d); onDateChange(d, d);
      },
    },
    {
      label: 'Неделя',
      fn: () => {
        const t = new Date();
        const f = new Date(t); f.setDate(f.getDate() - 6);
        const fd = f.toISOString().slice(0, 10);
        const td = t.toISOString().slice(0, 10);
        setFrom(fd); setTo(td); onDateChange(fd, td);
      },
    },
    {
      label: 'Месяц',
      fn: () => {
        const t = new Date();
        const fd = new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10);
        const td = t.toISOString().slice(0, 10);
        setFrom(fd); setTo(td); onDateChange(fd, td);
      },
    },
  ];

  return (
    <div style={{ padding: '0 0 32px' }}>
      <div style={ds.filterCard}>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
          {periods.map(p => (
            <button
              key={p.label}
              onClick={p.fn}
              style={ds.periodBtn}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="date"
            value={from}
            max={to}
            onChange={e => setFrom(e.target.value)}
            style={ds.dateInput}
          />
          <span style={{ color: '#999', fontSize: '13px' }}>—</span>
          <input
            type="date"
            value={to}
            min={from}
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => setTo(e.target.value)}
            style={ds.dateInput}
          />
          <button onClick={apply} style={ds.applyBtn}>Показать</button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>⏳ Загрузка...</div>
      )}

      {!loading && stats && (() => {
        const t = stats.total;
        if (!t) return null;

        return (
          <>
            <div style={ds.cardsGrid}>
              {[
                { label: 'Заказов', value: t.orders_count, color: '#007AFF', icon: '📋' },
                { label: 'Выручка', value: fmt(t.revenue) + ' сум', color: '#34C759', icon: '💰', small: true },
                { label: 'Доставлено', value: t.completed_count, color: '#8E8E93', icon: '✅' },
                { label: 'Отменено', value: t.cancelled_count, color: '#E85D4A', icon: '❌' },
              ].map((k, i) => (
                <div key={i} style={{ ...ds.summaryCard, backgroundColor: k.color }}>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>{k.icon}</div>
                  <div style={{ fontSize: k.small ? '15px' : '28px', fontWeight: '800', lineHeight: 1 }}>
                    {k.value}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>{k.label}</div>
                </div>
              ))}
            </div>

            {Number(t.orders_count) > 0 && (
              <div style={ds.infoCard}>
                <div style={ds.row}>
                  <span style={ds.rowLabel}>🧾 Средний чек</span>
                  <span style={ds.rowValue}>
                    {fmt(Math.round(Number(t.revenue) / Number(t.orders_count)))} сум
                  </span>
                </div>
                <div style={ds.divider} />
                <div style={ds.row}>
                  <span style={ds.rowLabel}>🚗 Выручка за доставку</span>
                  <span style={ds.rowValue}>{fmt(t.delivery_revenue)} сум</span>
                </div>
                <div style={ds.divider} />
                <div style={ds.row}>
                  <span style={ds.rowLabel}>🚗 Доставок</span>
                  <span style={ds.rowValue}>{t.delivery_count}</span>
                </div>
                <div style={ds.row}>
                  <span style={ds.rowLabel}>🏃 Самовывозов</span>
                  <span style={ds.rowValue}>{t.pickup_count}</span>
                </div>
              </div>
            )}

            {stats.topItems?.length > 0 && (
              <div style={ds.infoCard}>
                <div style={ds.sectionTitle}>🏆 Топ блюд</div>
                {stats.topItems.map((item, i) => (
                  <div key={i} style={ds.topItemRow(i < stats.topItems.length - 1)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        ...ds.rankBadge,
                        backgroundColor: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#F0F0F0',
                      }}>
                        {i + 1}
                      </div>
                      <span style={{ fontSize: '13px' }}>{item.name}</span>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#333' }}>{item.total_qty} шт</div>
                      <div style={{ fontSize: '12px', color: '#34C759', fontWeight: '600' }}>{fmt(item.total_sum)} сум</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {stats.byZone?.length > 0 && (
              <div style={ds.infoCard}>
                <div style={ds.sectionTitle}>🗺️ По районам</div>
                {stats.byZone.map((z, i) => {
                  const maxOrders = Math.max(...stats.byZone.map(x => Number(x.orders_count)));
                  const pct = maxOrders > 0 ? (Number(z.orders_count) / maxOrders) * 100 : 0;
                  return (
                    <div key={i} style={{ marginBottom: '10px' }}>
                      <div style={ds.row}>
                        <span style={{ fontSize: '13px', color: '#555' }}>{z.zone_name}</span>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700' }}>{fmt(z.revenue)} сум</span>
                          <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px' }}>{z.orders_count} зак.</span>
                        </div>
                      </div>
                      <div style={ds.barBg}>
                        <div style={{ ...ds.barFill, width: `${pct}%`, backgroundColor: '#007AFF' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {stats.byDay?.length > 0 && (
              <div style={ds.infoCard}>
                <div style={ds.sectionTitle}>📅 По дням</div>
                {stats.byDay.map((d, i) => {
                  const maxRev = Math.max(...stats.byDay.map(x => Number(x.revenue)));
                  const pct = maxRev > 0 ? (Number(d.revenue) / maxRev) * 100 : 0;
                  return (
                    <div key={i} style={{ marginBottom: '10px' }}>
                      <div style={ds.row}>
                        <span style={{ fontSize: '13px', color: '#555' }}>
                          {new Date(d.day).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' })}
                        </span>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '13px', fontWeight: '700' }}>{fmt(d.revenue)} сум</span>
                          <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px' }}>{d.orders_count} зак.</span>
                        </div>
                      </div>
                      <div style={{ ...ds.barBg, height: '6px' }}>
                        <div style={{ ...ds.barFill, height: '6px', width: `${pct}%`, backgroundColor: '#34C759' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {Number(t.orders_count) === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '40px', fontSize: '14px' }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>📭</div>
                За выбранный период заказов нет
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

const ds = {
  filterCard: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '14px', marginBottom: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  periodBtn: {
    flex: 1, padding: '7px 4px', borderRadius: '8px', border: '1px solid #E0E0E0',
    backgroundColor: '#F5F5F5', fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  dateInput: {
    flex: 1, padding: '8px 10px', border: '1px solid #E0E0E0',
    borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', outline: 'none',
  },
  applyBtn: {
    padding: '8px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#000',
    color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  cardsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px',
  },
  summaryCard: {
    borderRadius: '16px', padding: '16px', color: '#fff',
  },
  infoCard: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '14px', marginBottom: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  rowLabel: { fontSize: '14px', color: '#555' },
  rowValue: { fontSize: '15px', fontWeight: '700' },
  divider: { height: '1px', backgroundColor: '#F5F5F5', margin: '8px 0' },
  sectionTitle: { fontWeight: '700', fontSize: '14px', marginBottom: '12px' },
  topItemRow: (hasBorder) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0',
    borderBottom: hasBorder ? '1px solid #F5F5F5' : 'none',
  }),
  rankBadge: {
    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700',
  },
  barBg: { height: '5px', backgroundColor: '#F0F0F0', borderRadius: '3px' },
  barFill: { height: '5px', borderRadius: '3px', transition: 'width 0.4s ease' },
};

const s = {
  loading: { padding: '40px', textAlign: 'center', color: '#888' },
  message: {
    padding: '12px 16px', borderRadius: '12px',
    backgroundColor: '#E8F5E9', marginBottom: '12px',
    fontWeight: '500', textAlign: 'center',
  },
  subTabs: {
    display: 'flex', gap: '8px',
    marginBottom: '16px', overflowX: 'auto',
  },
  subTab: {
    padding: '8px 16px', borderRadius: '20px',
    border: '1px solid #E0E0E0', backgroundColor: '#fff',
    cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },
  subTabActive: {
    backgroundColor: '#000', color: '#fff', border: '1px solid #000',
  },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  card: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  cardTitle: {
    fontSize: '15px', fontWeight: '700', marginBottom: '12px',
  },
  toggle: {
    display: 'flex', alignItems: 'center', gap: '10px',
    fontSize: '14px', marginBottom: '10px', cursor: 'pointer',
  },
  label: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid #E0E0E0', fontSize: '14px',
    boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
  },
  deliveryUrl: {
    fontSize: '13px', color: '#666', backgroundColor: '#F5F5F5',
    padding: '8px 12px', borderRadius: '8px',
    wordBreak: 'break-all',
  },
  btnPrimary: {
    padding: '12px 20px', borderRadius: '10px', border: 'none',
    backgroundColor: '#000', color: '#fff', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600', fontFamily: 'inherit',
  },
  btnSecondary: {
    padding: '10px 16px', borderRadius: '10px',
    border: '1px solid #E0E0E0', backgroundColor: '#fff',
    cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit',
  },
  btnDashed: {
    width: '100%', padding: '12px', borderRadius: '12px',
    border: '2px dashed #ddd', backgroundColor: 'transparent',
    fontSize: '14px', color: '#666', cursor: 'pointer',
    marginTop: '8px', fontFamily: 'inherit',
  },
  btnIcon: {
    padding: '6px 10px', borderRadius: '8px', border: 'none',
    backgroundColor: '#F0F0F0', cursor: 'pointer', fontSize: '14px',
  },
  addForm: {
    backgroundColor: '#F9F9F9', borderRadius: '12px',
    padding: '12px', marginTop: '8px',
  },
  empty: {
    padding: '24px', textAlign: 'center',
    color: '#bbb', fontSize: '14px',
  },
  zoneRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px', backgroundColor: '#F9F9F9',
    borderRadius: '10px', marginBottom: '8px',
  },
  orderCard: {
    backgroundColor: '#fff', borderRadius: '16px',
    padding: '16px', marginBottom: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  orderHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '10px',
  },
  orderTitle: { fontSize: '15px', fontWeight: '700' },
  orderMeta: { fontSize: '13px', color: '#666', marginTop: '2px' },
  statusBadge: {
    padding: '4px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap',
  },
  orderRow: {
    fontSize: '13px', color: '#555',
    marginBottom: '6px', lineHeight: '1.4',
  },
  orderItems: {
    backgroundColor: '#F9F9F9', borderRadius: '10px',
    padding: '10px', margin: '10px 0',
  },
  orderItem: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '13px', padding: '4px 0',
    borderBottom: '1px solid #F0F0F0',
  },
  orderTotal: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '8px',
  },
  totalPrice: { fontSize: '18px', fontWeight: '800' },
  orderActions: {
    display: 'flex', gap: '8px', marginTop: '12px',
  },
  btnNext: {
    flex: 1, padding: '10px', borderRadius: '10px',
    border: 'none', backgroundColor: '#000', color: '#fff',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    fontFamily: 'inherit',
  },
  btnCancel: {
    padding: '10px 14px', borderRadius: '10px', border: 'none',
    backgroundColor: '#FFEBEE', color: '#E85D4A',
    cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit',
  },
  orderTime: {
    fontSize: '11px', color: '#bbb',
    marginTop: '8px', textAlign: 'right',
  },
};

// ─── Меню доставки ───────────────────────────────────────
function DeliveryMenuSection({ items, loading, api, showMsg, restaurantId, onRefresh }) {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [saving, setSaving] = useState({});

  const categories = useMemo(() => {
    const seen = new Map();
    items.forEach(item => {
      if (item.category_id && !seen.has(item.category_id)) {
        seen.set(item.category_id, item.category_name || 'Без категории');
      }
    });
    return [{ id: 'all', name: '📋 Все' }, ...Array.from(seen, ([id, name]) => ({ id, name }))];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(item => {
      const matchCat = filterCat === 'all' || String(item.category_id) === String(filterCat);
      const matchSearch = !search || (item.name || '').toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [items, search, filterCat]);

  const patch = async (itemId, field, value) => {
    setSaving(p => ({ ...p, [itemId + field]: true }));
    try {
      await api.patch(
        `/api/admin/delivery/menu/items/${itemId}${restaurantId ? `?restaurantId=${restaurantId}` : ''}`,
        { [field]: value }
      );
      showMsg('✅ Сохранено');
      onRefresh();
    } catch {
      showMsg('❌ Ошибка сохранения');
    } finally {
      setSaving(p => ({ ...p, [itemId + field]: false }));
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
      ⏳ Загружаем меню...
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Поиск по названию..."
          style={{
            flex: 1, minWidth: '160px', padding: '8px 12px',
            borderRadius: '10px', border: '1px solid #E0E0E0',
            fontSize: '14px', fontFamily: 'inherit',
          }}
        />
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '10px',
            border: '1px solid #E0E0E0', fontSize: '14px',
            fontFamily: 'inherit', backgroundColor: '#fff',
          }}
        >
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
        Показано: {filtered.length} из {items.length} блюд • Доступно для доставки: {items.filter(i => i.available_for_delivery).length}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filtered.map(item => (
          <DeliveryMenuItem
            key={item.id}
            item={item}
            saving={saving}
            onPatch={patch}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px', color: '#bbb' }}>Ничего не найдено</div>
        )}
      </div>
    </div>
  );
}

function DeliveryMenuItem({ item, saving, onPatch }) {
  const [editPrice, setEditPrice] = useState(false);
  const [priceVal, setPriceVal] = useState(String(item.delivery_price ?? item.price ?? ''));

  const isSavingAvail = saving[item.id + 'available_for_delivery'];
  const isSavingStop = saving[item.id + 'is_available'];
  const isSavingPrice = saving[item.id + 'delivery_price'];

  const displayPrice = item.delivery_price != null ? item.delivery_price : item.price;
  const hasPriceOverride = item.delivery_price != null && Number(item.delivery_price) !== Number(item.price);
  const overrideAvailable = item.override_available !== false;

  return (
    <div style={{
      backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #F0F0F0',
      padding: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start',
      opacity: !item.available_for_delivery ? 0.55 : 1, transition: 'opacity 0.2s',
    }}>
      {item.image_url ? (
        <img src={item.image_url} alt={item.name} style={{ width: '52px', height: '52px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
      ) : (
        <div style={{ width: '52px', height: '52px', borderRadius: '8px', backgroundColor: '#F5F5F5', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>🍽️</div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px' }}>{item.name}</div>
        <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>{item.category_name || 'Без категории'}</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          {editPrice ? (
            <>
              <input
                type="number"
                value={priceVal}
                onChange={e => setPriceVal(e.target.value)}
                style={{ width: '90px', padding: '4px 8px', borderRadius: '8px', border: '1px solid #2196F3', fontSize: '13px', fontFamily: 'inherit' }}
                autoFocus
              />
              <button onClick={() => { onPatch(item.id, 'delivery_price', priceVal === '' ? null : Number(priceVal)); setEditPrice(false); }} disabled={isSavingPrice} style={{ padding: '4px 10px', borderRadius: '8px', border: 'none', backgroundColor: '#2196F3', color: '#fff', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>{isSavingPrice ? '...' : '✓'}</button>
              <button onClick={() => { setEditPrice(false); setPriceVal(String(item.delivery_price ?? item.price ?? '')); }} style={{ padding: '4px 8px', borderRadius: '8px', border: '1px solid #ddd', backgroundColor: '#fff', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
              {item.delivery_price != null && (
                <button onClick={() => { onPatch(item.id, 'delivery_price', null); setEditPrice(false); }} style={{ padding: '4px 8px', borderRadius: '8px', border: '1px solid #FF5252', backgroundColor: '#fff', color: '#FF5252', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>Сбросить</button>
              )}
            </>
          ) : (
            <>
              <span style={{ fontWeight: '700', fontSize: '15px' }}>{Number(displayPrice).toLocaleString()} сум</span>
              {hasPriceOverride && <span style={{ fontSize: '11px', color: '#FF9800', fontWeight: '600' }}>(своя цена, базовая: {Number(item.price).toLocaleString()})</span>}
              <button onClick={() => { setPriceVal(String(item.delivery_price ?? item.price ?? '')); setEditPrice(true); }} style={{ padding: '2px 8px', borderRadius: '6px', border: '1px solid #E0E0E0', backgroundColor: '#F9F9F9', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit', color: '#555' }}>✏️ Изменить цену</button>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', padding: '4px 10px', borderRadius: '8px', backgroundColor: item.available_for_delivery ? '#E8F5E9' : '#FFEBEE', color: item.available_for_delivery ? '#2E7D32' : '#C62828', fontWeight: '600' }}>
            <input type="checkbox" checked={!!item.available_for_delivery} disabled={isSavingAvail} onChange={e => onPatch(item.id, 'available_for_delivery', e.target.checked)} style={{ accentColor: '#4CAF50' }} />
            {isSavingAvail ? '...' : (item.available_for_delivery ? '✅ В меню доставки' : '❌ Скрыто')}
          </label>
          {item.available_for_delivery && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer', padding: '4px 10px', borderRadius: '8px', backgroundColor: overrideAvailable ? '#E3F2FD' : '#FFF3E0', color: overrideAvailable ? '#1565C0' : '#E65100', fontWeight: '600' }}>
              <input type="checkbox" checked={overrideAvailable} disabled={isSavingStop} onChange={e => onPatch(item.id, 'is_available', e.target.checked)} style={{ accentColor: '#2196F3' }} />
              {isSavingStop ? '...' : (overrideAvailable ? '🟢 В наличии' : '🔴 Стоп-лист')}
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
